// Inbound mail HTML is written by whoever sent the message, so every byte of
// it is attacker-controlled. Until 2026-10 the app put message bodies straight
// into the page with dangerouslySetInnerHTML, which ran any <img onerror>,
// <svg onload> or javascript: link a sender cared to include, with the
// reader's api.workpeg.com session.
//
// This module is the only way a message body reaches the page. Three layers,
// each enough to stop script on its own:
//   1 DOMPurify, configured below, removes every script-bearing tag,
//     attribute and URL.
//   2 mailFrameDocument() wraps the result in a document whose
//     Content-Security-Policy forbids script and, while remote content is
//     blocked, every remote load.
//   3 MailHtmlFrame renders that document in an iframe sandbox WITHOUT
//     allow-scripts, so a sanitiser miss still cannot execute.
//
// Remote images are blocked by default (tracking pixels report when and where
// a message was read). The reader can show them per message, which re-runs
// the sanitiser on the ORIGINAL body with allowRemoteImages: the
// data-blocked-* attributes left behind are never copied back into a live
// attribute, so a sender cannot smuggle a URL through them.

import createDOMPurify from 'dompurify';

// Tags that execute, embed another document, submit data or change how the
// frame resolves URLs. DOMPurify drops most of them already; naming them keeps
// that true if its defaults ever change.
const FORBID_TAGS = [
  'script', 'iframe', 'frame', 'frameset', 'object', 'embed', 'form', 'input',
  'button', 'textarea', 'select', 'base', 'meta', 'link',
];
const FORBID_ATTR = ['formaction', 'srcdoc', 'action', 'ping'];

const CONFIG = {
  USE_PROFILES: { html: true }, // no SVG or MathML: mail has no use for them
  WHOLE_DOCUMENT: true, // keep <head><style>: newsletters are styled there
  FORBID_TAGS,
  FORBID_ATTR,
  RETURN_DOM: true,
};

// The browser ignores ASCII whitespace and control characters inside a URL
// ("java\tscript:" is "javascript:"), so the scheme is read without them.
// The same class DOMPurify uses for its own URL checks.
// eslint-disable-next-line no-control-regex
const URL_WHITESPACE = /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g;
const LINK_URL = /^(?:https?|mailto|tel):/i;
const RESOURCE_URL = /^(?:https?:|cid:|data:image\/(?:png|gif|jpeg|webp)[;,])/i;
const REMOTE_URL = /^https?:/i;
const LINK_TAGS = new Set(['A', 'AREA']);
// Attributes that make the browser fetch something as soon as it renders.
const RESOURCE_ATTRS = ['src', 'srcset', 'background', 'poster'];

const LINK_REL = 'noopener noreferrer nofollow';

// The sandbox of the frame a message renders in (MailHtmlFrame). It has NO
// allow-scripts: even HTML the sanitiser failed to clean cannot run, and
// allow-scripts together with allow-same-origin would let the frame remove
// its own sandbox. allow-same-origin is there only so the app can read the
// frame's height and keep the mail inline instead of in a scroll box; with
// scripts off, nothing inside the frame can use that origin. allow-popups
// (with escape-sandbox, so the opened site works normally) lets a reader
// follow a link, which the sanitiser has already given target=_blank and
// rel="noopener noreferrer nofollow". No forms, no top navigation, no modals.
export const MAIL_FRAME_SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox';

export const normaliseUrl = (url) => String(url).replace(URL_WHITESPACE, '');

export const isSafeLink = (url) => LINK_URL.test(normaliseUrl(url));

// The URLs of a srcset candidate list. A data: URL may itself contain commas,
// so this follows the HTML parsing rule (a URL runs to the next whitespace,
// trailing commas end the candidate) rather than splitting on ','.
export const srcsetUrls = (value) => {
  const urls = [];
  const text = String(value);
  let i = 0;
  while (i < text.length) {
    while (i < text.length && /[\s,]/.test(text[i])) i += 1;
    if (i >= text.length) break;
    const start = i;
    while (i < text.length && !/\s/.test(text[i])) i += 1;
    const url = text.slice(start, i);
    if (/,$/.test(url)) {
      urls.push(url.replace(/,+$/, ''));
      continue;
    }
    urls.push(url);
    let depth = 0;
    while (i < text.length) {
      const ch = text[i];
      i += 1;
      if (ch === '(') depth += 1;
      else if (ch === ')') depth = Math.max(0, depth - 1);
      else if (ch === ',' && depth === 0) break;
    }
  }
  return urls;
};

// 'drop' (a scheme that is not allowed), 'remote' (fetched from the network)
// or 'local' (cid: and data:image, which never leave the browser).
const resourceVerdict = (urls) => {
  const clean = urls.map(normaliseUrl);
  if (clean.length === 0 || clean.some((url) => !RESOURCE_URL.test(url))) return 'drop';
  return clean.some((url) => REMOTE_URL.test(url)) ? 'remote' : 'local';
};

const CSS_IMPORT = /@import\b[^;]*;?/gi;
const CSS_URL = /url\(\s*(['"]?)([\s\S]*?)\1\s*\)/gi;
const CSS_LOCAL_URL = /^\s*(?:data:|cid:)/i;

// Removes every remote fetch a stylesheet can make with url() or @import.
// Obfuscated forms (CSS escapes, image-set strings) are left to the frame's
// Content-Security-Policy, which refuses the request whatever its spelling.
export const stripRemoteCss = (css) => {
  let removed = 0;
  const out = String(css)
    .replace(CSS_IMPORT, () => {
      removed += 1;
      return '';
    })
    .replace(CSS_URL, (match, quote, target) => {
      if (CSS_LOCAL_URL.test(target)) return match;
      removed += 1;
      return 'none';
    });
  return { css: out, removed };
};

// The frame's own Content-Security-Policy. default-src 'none' covers script,
// frames, objects, fetches and form targets; the rest only reopen what mail
// needs. 'self' is the app's origin, for the Poppins font the app serves.
export const frameCsp = (allowRemoteImages) => {
  const remote = allowRemoteImages ? ' https: http:' : '';
  return [
    "default-src 'none'",
    "script-src 'none'",
    `img-src data: cid:${remote}`,
    `media-src data:${remote}`,
    `style-src 'unsafe-inline'${remote}`,
    `font-src 'self' data:${remote}`,
    "form-action 'none'",
  ].join('; ');
};

// What the old .email-content-wrapper rules in globals.css did, plus the
// app's body text and its dark-mode colour (tailwind grey-900 and
// grey-dark-900). Constant text: nothing from the message reaches it.
//
// color-scheme ties the frame's canvas to the same preference as its text
// colour. Without it the frame is a light document, and inside the dark app
// Chrome paints it an opaque white canvas under the light dark-mode text.
const FRAME_CSS = `
@font-face { font-family: 'Poppins'; src: url('/fonts/Poppins-Regular.ttf') format('truetype'); font-weight: 400; font-display: swap; }
:root { color-scheme: light dark; }
html { overflow-y: hidden; }
html, body { margin: 0; padding: 0; }
body { display: flow-root; font-family: 'Poppins', system-ui, sans-serif; font-weight: 400; color: #0d0d0e; overflow-wrap: break-word; }
body.wp-wide { font-size: 16px; line-height: 24px; }
body.wp-compact { font-size: 14px; line-height: 22px; }
@media (prefers-color-scheme: dark) { body { color: #DEDFE0; } }
body * { max-width: 100% !important; height: auto !important; }
p { margin: 0; }
a { color: inherit; text-decoration: underline; }
table { width: 100% !important; max-width: 100% !important; border-collapse: collapse; }
td { padding: 8px !important; word-wrap: break-word; overflow-wrap: break-word; }
body.wp-compact td { padding: 4px !important; }
img { max-width: 100% !important; height: auto !important; display: block; }
`;

export function createMailHtml(win) {
  const purify = createDOMPurify(win);
  let allowRemote = false;
  let blocked = 0;

  purify.addHook('afterSanitizeAttributes', (node) => {
    if (LINK_TAGS.has(node.nodeName)) {
      if (node.hasAttribute('href') && !isSafeLink(node.getAttribute('href'))) {
        node.removeAttribute('href');
      }
      // Removed first so a second pass writes them in the same order.
      node.removeAttribute('target');
      node.removeAttribute('rel');
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', LINK_REL);
    } else if (node.hasAttribute('href')) {
      // Outside <a>/<area> an href is a resource (an <image>, a <use>).
      node.removeAttribute('href');
    }

    for (const name of RESOURCE_ATTRS) {
      if (!node.hasAttribute(name)) continue;
      const value = node.getAttribute(name);
      const verdict = resourceVerdict(name === 'srcset' ? srcsetUrls(value) : [value]);
      if (verdict === 'drop') {
        node.removeAttribute(name);
      } else if (verdict === 'remote' && !allowRemote) {
        node.removeAttribute(name);
        node.setAttribute(`data-blocked-${name}`, value);
        blocked += 1;
      }
    }

    if (!allowRemote && node.hasAttribute('style')) {
      const { css, removed } = stripRemoteCss(node.getAttribute('style'));
      if (removed) {
        blocked += removed;
        node.setAttribute('style', css);
      }
    }

    if (!allowRemote && node.nodeName === 'STYLE') {
      const { css, removed } = stripRemoteCss(node.textContent || '');
      if (removed) {
        blocked += removed;
        node.textContent = css;
      }
    }
  });

  const sanitize = (html, allowRemoteImages) => {
    if (!purify.isSupported) return { root: null, blockedCount: 0 };
    allowRemote = Boolean(allowRemoteImages);
    blocked = 0;
    const root = purify.sanitize(html == null ? '' : String(html), CONFIG);
    return { root, blockedCount: blocked };
  };

  // { html, blockedCount }: html is the sanitised message as a whole
  // document (<html><head>...</head><body>...</body></html>).
  const sanitizeMailHtml = (html, { allowRemoteImages = false } = {}) => {
    const { root, blockedCount } = sanitize(html, allowRemoteImages);
    return { html: root ? root.outerHTML : '', blockedCount };
  };

  // { html, blockedCount }: html is the srcdoc for MailHtmlFrame, the
  // sanitised message with the frame's policy, referrer, base target and
  // styles placed first in its <head>. Built with DOM calls, so nothing from
  // the message is ever concatenated into markup.
  const mailFrameDocument = (html, { allowRemoteImages = false, compact = false } = {}) => {
    const { root, blockedCount } = sanitize(html, allowRemoteImages);
    if (!root) return { html: '', blockedCount: 0 };
    const doc = root.ownerDocument;
    let head = root.querySelector('head');
    if (!head) {
      head = doc.createElement('head');
      root.insertBefore(head, root.firstChild);
    }
    let body = root.querySelector('body');
    if (!body) {
      body = doc.createElement('body');
      root.appendChild(body);
    }

    const csp = doc.createElement('meta');
    csp.setAttribute('http-equiv', 'Content-Security-Policy');
    csp.setAttribute('content', frameCsp(allowRemoteImages));
    const referrer = doc.createElement('meta');
    referrer.setAttribute('name', 'referrer');
    referrer.setAttribute('content', 'no-referrer');
    const base = doc.createElement('base');
    base.setAttribute('target', '_blank');
    const style = doc.createElement('style');
    style.textContent = FRAME_CSS;
    head.insertBefore(style, head.firstChild);
    head.insertBefore(base, style);
    head.insertBefore(referrer, base);
    head.insertBefore(csp, referrer);

    body.classList.add(compact ? 'wp-compact' : 'wp-wide');
    return { html: `<!doctype html>${root.outerHTML}`, blockedCount };
  };

  return { sanitizeMailHtml, mailFrameDocument };
}

// The browser instance, created on first use. During the static export there
// is no window, and the answer is an empty body rather than unsanitised HTML.
let browserInstance = null;
const browser = () => {
  if (!browserInstance && typeof window !== 'undefined') browserInstance = createMailHtml(window);
  return browserInstance;
};

export const sanitizeMailHtml = (html, options) => {
  const instance = browser();
  return instance ? instance.sanitizeMailHtml(html, options) : { html: '', blockedCount: 0 };
};

export const mailFrameDocument = (html, options) => {
  const instance = browser();
  return instance ? instance.mailFrameDocument(html, options) : { html: '', blockedCount: 0 };
};
